import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Gauge, Wrench, AlertTriangle, FileSearch, ClipboardCheck, ShieldCheck, Camera,
  FileText, ArrowLeftRight, Stamp, Truck, Cog, Paintbrush, OctagonAlert, BadgeCheck, Paperclip, ArrowDown,
} from "lucide-react";
import { api, photoUrl } from "../api";
import { fmtDate, fmtKm } from "../lib/labels";

const TYPE_META = {
  tescil: { label: "İlk Kayıt", Icon: Stamp, color: "text-sky-400", ring: "border-sky-500/40 bg-sky-500/10" },
  km_okuma: { label: "Kilometre", Icon: Gauge, color: "text-sky-300", ring: "border-sky-400/40 bg-sky-400/10" },
  servis: { label: "Servis / Bakım", Icon: Wrench, color: "text-emerald-400", ring: "border-emerald-500/40 bg-emerald-500/10" },
  kaza: { label: "Hasar", Icon: AlertTriangle, color: "text-red-400", ring: "border-red-500/40 bg-red-500/10" },
  hasar: { label: "Hasar", Icon: AlertTriangle, color: "text-amber-400", ring: "border-amber-500/40 bg-amber-500/10" },
  ekspertiz: { label: "Ekspertiz", Icon: FileSearch, color: "text-emerald-300", ring: "border-emerald-400/40 bg-emerald-400/10" },
  muayene: { label: "Muayene", Icon: ClipboardCheck, color: "text-cyan-400", ring: "border-cyan-500/40 bg-cyan-500/10" },
  sigorta: { label: "Sigorta", Icon: ShieldCheck, color: "text-teal-300", ring: "border-teal-400/40 bg-teal-400/10" },
  parca_degisimi: { label: "Parça Değişimi", Icon: Cog, color: "text-slate-300", ring: "border-slate-400/40 bg-slate-400/10" },
  boya: { label: "Boya", Icon: Paintbrush, color: "text-amber-300", ring: "border-amber-400/40 bg-amber-400/10" },
  sahiplik: { label: "Sahiplik Değişikliği", Icon: ArrowLeftRight, color: "text-violet-300", ring: "border-violet-400/40 bg-violet-400/10" },
  cekici: { label: "Çekici", Icon: Truck, color: "text-orange-300", ring: "border-orange-400/40 bg-orange-400/10" },
  km_anomali: { label: "KM Tutarsızlığı", Icon: OctagonAlert, color: "text-red-400", ring: "border-red-500/40 bg-red-500/10" },
  fotograf: { label: "Fotoğraf", Icon: Camera, color: "text-slate-300", ring: "border-white/15 bg-white/5" },
  belge: { label: "Belge", Icon: FileText, color: "text-slate-300", ring: "border-white/15 bg-white/5" },
};

export function HistorySummary({ summary }) {
  const cards = [
    { label: "Doğrulanmış Kayıt", value: summary.dogrulanmis_kayit, testid: "summary-dogrulanmis" },
    { label: "Sahiplik Değişimi", value: summary.sahiplik_degisimi, testid: "summary-sahiplik" },
    { label: "Servis Kaydı", value: summary.servis_kaydi, testid: "summary-servis" },
    { label: "Belgeli Kayıt", value: summary.belgeli_kayit, testid: "summary-belgeli" },
    { label: "Son Kayıtlı KM", value: summary.son_km != null ? fmtKm(summary.son_km) : "—", testid: "summary-son-km" },
  ];
  return (
    <div data-testid="history-summary-v2" className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {cards.map((c) => (
        <div key={c.testid} data-testid={c.testid} className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider">{c.label}</p>
          <p className="text-xl font-bold text-cyan-300 mt-1">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

export function HistoryTimelineV2({ timeline }) {
  if (!timeline.length) {
    return <p className="text-slate-500 text-sm">Bu araç için kayıt bulunmuyor.</p>;
  }
  return (
    <div data-testid="history-timeline-v2" className="space-y-0">
      {timeline.map((e, i) => {
        const meta = TYPE_META[e.type] || TYPE_META.tescil;
        const year = (e.date || "").slice(0, 4);
        return (
          <div key={i} data-testid={`timeline-v2-item-${i}`} className="relative">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 ${meta.ring}`}>
                  <meta.Icon className={`w-[18px] h-[18px] ${meta.color}`} />
                </span>
                {i < timeline.length - 1 && (
                  <span className="flex-1 w-px bg-gradient-to-b from-cyan-500/40 to-slate-700/40 my-1 min-h-6 relative">
                    <ArrowDown className="w-3 h-3 text-slate-600 absolute -left-[5px] bottom-0" />
                  </span>
                )}
              </div>
              <div className="flex-1 rounded-xl border border-white/10 bg-slate-900/60 p-4 mb-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 justify-between">
                  <h4 className="font-semibold text-slate-100 text-sm">
                    {year && <span className="text-cyan-400 font-mono-vin mr-2">{year}</span>}
                    {e.title}
                  </h4>
                  <span className="text-xs font-mono-vin text-slate-400">{fmtDate(e.date)}</span>
                </div>
                {e.description && <p className="text-sm text-slate-400 mt-1 leading-relaxed">{e.description}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${meta.ring} ${meta.color}`}>{e.type_label}</span>
                  {e.km != null && (
                    <span data-testid={`timeline-v2-km-${i}`} className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-slate-300">{fmtKm(e.km)}</span>
                  )}
                  {e.verified && (
                    <span data-testid={`timeline-v2-verified-${i}`} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
                      <BadgeCheck className="w-3 h-3" /> Doğrulandı
                    </span>
                  )}
                  {e.has_document && e.documents.map((d, di) => (
                    <a key={di} data-testid={`timeline-v2-doc-${i}-${di}`} href={photoUrl(d)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-colors">
                      <Paperclip className="w-3 h-3" /> Belge / Görsel
                    </a>
                  ))}
                  <span className="text-[10px] px-2 py-1 rounded-full border border-white/5 text-slate-600">{e.period}. Dönem</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-slate-600 text-center pt-2">
        Sahibi değişir. Geçmişi değişmez. — Geçmiş kayıtlar kişisel sahip bilgisi içermez.
      </p>
    </div>
  );
}

export default function HistoryReportV2({ vin }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/vehicles/${vin}/timeline-v2`).then((r) => { if (!cancelled) setData(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [vin]);
  if (!data) {
    return <div className="p-8 flex justify-center"><div className="w-7 h-7 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>;
  }
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-4">Sorgu Özeti</h2>
        <HistorySummary summary={data.summary} />
      </div>
      <div>
        <h2 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-6">Araç Geçmişi — Zaman Çizelgesi</h2>
        <HistoryTimelineV2 timeline={data.timeline} />
      </div>
    </div>
  );
}
